import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Eye, EyeOff, Info } from 'lucide-react';
import { withAuthHeaders } from '../utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

function InputWithEye({ label, placeholder, value, onChange, testId, hint }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="input-group">
      <label className="input-label">{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          className="input"
          type={visible ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          style={{ paddingRight: 42 }}
          data-testid={testId}
        />
        <button
          type="button"
          className="btn-eye"
          onClick={() => setVisible(!visible)}
          data-testid={`${testId}-toggle`}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {hint && <p className="input-hint">{hint}</p>}
    </div>
  );
}

export default function SettingsPage({ settings, onUpdate, auth }) {
  const [form, setForm] = useState({
    service_name: settings?.service_name || '',
    main_bot_username: settings?.main_bot_username || '',
    bot_token: settings?.bot_token || '',
    remnawave_api_url: settings?.remnawave_api_url || '',
    remnawave_api_token: settings?.remnawave_api_token || '',
    remnawave_readonly_token: settings?.remnawave_readonly_token || '',
    allowed_manager_ids: (settings?.allowed_manager_ids || []).join(', '),
    support_group_id: settings?.support_group_id || '',
    mini_app_domain: settings?.mini_app_domain || '',
    bedolaga_webhook_url: settings?.bedolaga_webhook_url || settings?.bedolaga_api_url || '',
    bedolaga_web_api_token: settings?.bedolaga_web_api_token || settings?.bedolaga_api_token || '',
    wata_access_token: settings?.wata_access_token || '',
    bedolaga_bot_token: settings?.bedolaga_bot_token || '',
    wata_base_url: settings?.wata_base_url || '',
    wata_min_amount_kopeks: settings?.wata_min_amount_kopeks ?? '',
    wata_max_amount_kopeks: settings?.wata_max_amount_kopeks ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const handleChange = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const headers = withAuthHeaders(auth, { 'Content-Type': 'application/json' });

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const payload = {
        ...form,
        allowed_manager_ids: form.allowed_manager_ids.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)),
        support_group_id: form.support_group_id ? parseInt(form.support_group_id) : null,
        wata_min_amount_kopeks: form.wata_min_amount_kopeks === '' ? null : parseInt(form.wata_min_amount_kopeks),
        wata_max_amount_kopeks: form.wata_max_amount_kopeks === '' ? null : parseInt(form.wata_max_amount_kopeks),
      };
      const r = await fetch(`${API}/api/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });
      const data = await r.json();
      if (data.ok) {
        setMsg('Настройки сохранены');
        onUpdate();
      } else {
        setMsg('Ошибка: ' + (data.error || 'unknown'));
      }
    } catch (e) {
      setMsg('Ошибка сети');
    } finally {
      setSaving(false);
    }
  };


  const textFields = [
    { key: 'service_name', label: 'Название сервиса', placeholder: 'My VPN', hint: 'Бренд: шапка панели, сообщения бота и промпт AI ({service_name})' },
    { key: 'main_bot_username', label: 'Username основного бота', placeholder: 'YourVPNBot', hint: 'Бот для покупки подписки. Используется как {main_bot}' },
    { key: 'allowed_manager_ids', label: 'ID менеджеров (через запятую)', placeholder: '123456789, 987654321' },
    { key: 'support_group_id', label: 'ID группы поддержки', placeholder: '-1001234567890' },
    { key: 'mini_app_domain', label: 'Домен Mini App', placeholder: 'app.example.com' },
    { key: 'wata_base_url', label: 'WATA API URL', placeholder: 'https://api.wata.pro/api/h2h', hint: 'Пусто — адрес по умолчанию' },
    { key: 'wata_min_amount_kopeks', label: 'Мин. сумма пополнения, коп.', placeholder: '1000', hint: '1000 = 10 ₽. Пусто — без ограничения' },
    { key: 'wata_max_amount_kopeks', label: 'Макс. сумма пополнения, коп.', placeholder: '500000', hint: '500000 = 5000 ₽' },
  ];

  const secretFields = [
    { key: 'bot_token', label: 'Bot Token', placeholder: 'xxxxx:yyyyy' },
    { key: 'remnawave_api_url', label: 'Remnawave API URL', placeholder: 'https://api.example.com' },
    { key: 'remnawave_api_token', label: 'Remnawave API Token', placeholder: 'Bearer token' },
    { key: 'remnawave_readonly_token', label: 'Remnawave API Token (только чтение, для AI)', placeholder: 'Необязательно — иначе используется основной' },
    { key: 'bedolaga_webhook_url', label: 'Bedolaga WEBHOOK_URL', placeholder: 'https://bedolaga.example.com' },
    { key: 'bedolaga_web_api_token', label: 'Bedolaga WEB_API_DEFAULT_TOKEN', placeholder: 'API key' },
    { key: 'bedolaga_bot_token', label: 'Токен основного бота Bedolaga (автоплатёж и продление через AI)', placeholder: '123456:ABC...' },
    { key: 'wata_access_token', label: 'WATA Access Token (пополнение через AI)', placeholder: 'Токен H2H API WATA' },
  ];

  return (
    <div data-testid="settings-page">
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Основные настройки</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 4 }}>
          Конфигурация бота и API. Все изменения сохраняются в базу данных.
        </p>
      </div>

      {msg && (
        <div className={`alert ${msg.includes('Ошибка') ? 'alert-error' : 'alert-success'}`} data-testid="settings-msg">
          {msg}
        </div>
      )}

      <div className="card">
        {textFields.map(f => (
          <div className="input-group" key={f.key}>
            <label className="input-label">{f.label}</label>
            <input
              className="input"
              type="text"
              placeholder={f.placeholder}
              value={form[f.key]}
              onChange={e => handleChange(f.key, e.target.value)}
              data-testid={`setting-${f.key}`}
            />
            {f.hint && <p className="input-hint">{f.hint}</p>}
          </div>
        ))}

        {secretFields.map(f => (
          <InputWithEye
            key={f.key}
            label={f.label}
            placeholder={f.placeholder}
            value={form[f.key]}
            onChange={e => handleChange(f.key, e.target.value)}
            testId={`setting-${f.key}`}
          />
        ))}

        <div className="alert alert-info" style={{ marginBottom: 14 }}>
          <Info size={13} style={{ marginRight: 6, verticalAlign: -2 }} />
          Системный промпт теперь собирается из блоков: <b>AI → Промпт</b> (стиль, факты, выученные правила, версии).
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <><RefreshCw size={16} className="loading-spinner" /> Сохранение...</> : <><Save size={16} /> Сохранить</>}
          </button>
        </div>
      </div>
    </div>
  );
}
